import Button from '../../../components/Button';
import classNames from './styles.module.scss';

// ─── Constants ───────────────────────────────────────────────────────────────

const DASHARRAY = 226.2; // 2π × 36

// ─── Countdown Ring ──────────────────────────────────────────────────────────

const CountdownRing = ({ state, daysUntil, color, t }) => {
  const dashoffset = (() => {
    if (state === 'far-out') return 0;
    if (state === 'due-today' || state === 'overdue') return 215;
    if (state === 'approved') {
      const progress = Math.max(0, Math.min(1, Math.abs(daysUntil) / 7));
      return DASHARRAY * (1 - progress);
    }
    // on-track: drain over 7-day window
    const progress = Math.max(0, Math.min(1, daysUntil / 7));
    return DASHARRAY * (1 - progress);
  })();

  const numLabel = state === 'due-today' ? '0' : String(Math.abs(daysUntil));
  const unitLabel =
    state === 'due-today'
      ? t('ringUnitToday')
      : state === 'overdue'
      ? t('ringUnitDaysAgo')
      : t('ringUnitDaysLeft');

  return (
    <div className={classNames.ringWrap}>
      <svg
        viewBox="0 0 90 90"
        width="90"
        height="90"
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        <circle className={classNames.ringTrack} cx="45" cy="45" r="36" />
        <circle
          className={classNames.ringFill}
          cx="45"
          cy="45"
          r="36"
          stroke={color}
          strokeDasharray={DASHARRAY}
          strokeDashoffset={dashoffset}
        />
      </svg>
      <div className={classNames.ringInner}>
        <span className={classNames.ringNum} style={{ color }}>
          {numLabel}
        </span>
        <span className={classNames.ringUnit}>{unitLabel}</span>
      </div>
    </div>
  );
};

// ─── Submitted / Approved icon (no ring) ─────────────────────────────────────

const CheckIcon = () => (
  <div className={classNames.checkIcon} aria-hidden="true">
    ✅
  </div>
);

// ─── State config ─────────────────────────────────────────────────────────────

const STATE_CONFIG = {
  'far-out': {
    headerClass: 'heroHeaderSafe',
    cardBorderColor: '#14B8A6',
    eyebrowKey: 'heroEyebrowDeadlineApproaching',
    ringColor: '#14B8A6',
    titleKey: 'heroTitleTripsDueIn',
    descKey: 'heroDescFarOut',
    primaryLabelKey: 'submitMyTrips',
    primaryType: 'teal',
    showRing: true,
  },
  'on-track': {
    headerClass: 'heroHeaderSafe',
    cardBorderColor: '#14B8A6',
    eyebrowKey: 'heroEyebrowDeadlineApproaching',
    ringColor: '#14B8A6',
    titleKey: 'heroTitleTripsDueIn',
    descKey: 'heroDescOnTrack',
    primaryLabelKey: 'submitMyTrips',
    primaryType: 'teal',
    showRing: true,
  },
  'due-today': {
    headerClass: 'heroHeaderUrgent',
    cardBorderColor: '#DC2626',
    eyebrowKey: 'heroEyebrowActionRequired',
    ringColor: '#DC2626',
    titleKey: 'heroTitleTripsDueToday',
    descKey: 'heroDescDueToday',
    primaryLabelKey: 'submitMyTrips',
    primaryType: 'danger',
    showRing: true,
  },
  overdue: {
    headerClass: 'heroHeaderUrgent',
    cardBorderColor: '#DC2626',
    eyebrowKey: 'heroEyebrowDeadlineMissed',
    ringColor: '#DC2626',
    titleKey: 'heroTitleDeadlinePassed',
    descKey: 'heroDescOverdue',
    primaryLabelKey: 'submitMyTrips',
    primaryType: 'danger',
    showRing: true,
  },
  submitted: {
    headerClass: 'heroHeaderDone',
    cardBorderColor: '#16A34A',
    eyebrowKey: 'heroEyebrowTripsSubmitted',
    ringColor: '#16A34A',
    titleKey: 'heroTitleAwaitingReview',
    descKey: 'heroDescSubmitted',
    primaryLabelKey: 'viewSubmittedTrips',
    primaryType: 'ghost',
    showRing: false,
  },
  approved: {
    headerClass: 'heroHeaderDone',
    cardBorderColor: '#16A34A',
    eyebrowKey: 'heroEyebrowReimbursementApproved',
    ringColor: '#16A34A',
    titleKey: 'heroTitleTripsApproved',
    descKey: 'heroDescApproved',
    primaryLabelKey: 'viewApprovedTrips',
    primaryType: 'ghost',
    showRing: true,
  },
};

// ─── HeroCard ─────────────────────────────────────────────────────────────────

const HeroCard = ({
  t,
  state,
  daysUntil,
  deadlineFormatted,
  approvalFormatted,
  paymentFormatted,
  periodRange,
  submittedDateFormatted,
  approvedDateFormatted,
  onSubmitTrips,
  onViewTrips,
  onViewSubmittedTrips,
}) => {
  const config = STATE_CONFIG[state];
  if (!config) return null;

  const title = (() => {
    if (state === 'overdue') return t(config.titleKey, { count: Math.abs(daysUntil) });
    if (state === 'far-out' || state === 'on-track') return t(config.titleKey, { days: daysUntil });
    return t(config.titleKey);
  })();

  const desc = (() => {
    if (state === 'submitted')
      return t(config.descKey, { approvalDate: approvalFormatted, paymentDate: paymentFormatted });
    if (state === 'approved') return t(config.descKey, { paymentDate: paymentFormatted });
    return t(config.descKey, { date: deadlineFormatted });
  })();

  return (
    <div className={classNames.deadlineHero} style={{ borderColor: config.cardBorderColor }}>
      <div className={[classNames.heroHeader, classNames[config.headerClass]].join(' ')}>
        <span className={classNames.heroEyebrow}>{t(config.eyebrowKey)}</span>
        {periodRange && <span className={classNames.heroPeriod}>{periodRange}</span>}
      </div>

      <div className={classNames.heroBody}>
        {/* Left: ring or check icon */}
        {config.showRing ? (
          <CountdownRing state={state} daysUntil={daysUntil} color={config.ringColor} t={t} />
        ) : (
          <CheckIcon />
        )}

        {/* Right: content */}
        <div className={classNames.heroContent}>
          {/* Submitted / approved badge */}
          {state === 'submitted' && submittedDateFormatted && (
            <div className={classNames.submittedBadge}>
              ✓&nbsp;&nbsp;{t('submitted')} {submittedDateFormatted}
            </div>
          )}
          {state === 'approved' && approvedDateFormatted && (
            <div className={classNames.submittedBadge}>
              ✓✓&nbsp;&nbsp;{t('approved')} {approvedDateFormatted}
            </div>
          )}

          <div
            className={classNames.heroTitle}
            style={state === 'submitted' || state === 'approved' ? { color: '#15803D' } : undefined}
          >
            {title}
          </div>
          <div className={classNames.heroDesc}>{desc}</div>

          <div className={classNames.heroActions}>
            {config.primaryType !== 'ghost' ? (
              <Button type={config.primaryType} onClick={onSubmitTrips}>
                {t(config.primaryLabelKey)} →
              </Button>
            ) : (
              <Button
                type="ghost"
                onClick={state === 'submitted' ? onViewSubmittedTrips : onViewTrips}
              >
                {t(config.primaryLabelKey)}
              </Button>
            )}
            {(state === 'far-out' ||
              state === 'on-track' ||
              state === 'due-today' ||
              state === 'overdue') && (
              <Button type="ghost" onClick={onViewTrips}>
                {t('viewMyTrips')}
              </Button>
            )}
            {state === 'submitted' && (
              <Button type="ghost" onClick={onSubmitTrips}>
                {t('submitMoreTrips')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroCard;
